import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { Message } from "./Message";
import { ChevronDown } from "lucide-react";
import { t } from "@/lib/i18n";

export interface MessageData {
  _id: string;
  content: string;
  authorName: string;
  timestamp: number;
  authorId: string;
  authorPictureUrl?: string | null;
  authorActiveFrameUrl?: string | null;
  authorTag?: string | null;
  authorDomainType?: string | null;
}

type MessageListProps = {
  messages?: MessageData[];
  currentUserId?: string;
};

export function MessageList({
  messages: passedMessages,
  currentUserId: passedCurrentUserId,
}: MessageListProps) {
  const queriedMessages = (useQuery(api.messages.list) || []) as MessageData[];
  const queriedCurrentUser = useQuery(api.users.current);

  const messages = passedMessages ?? queriedMessages;
  const currentUserId = passedCurrentUserId ?? queriedCurrentUser?._id;
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const hasScrolledOnOpen = useRef(false);
  const lastSeenIdRef = useRef<string | null>(null);
  const isAtBottomRef = useRef(true);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [newMessageCount, setNewMessageCount] = useState(0);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleScrollToBottomClick = () => {
    scrollToBottom();
    setNewMessageCount(0);
  };

  const handleScroll = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 100;
    isAtBottomRef.current = atBottom;
    setIsAtBottom(atBottom);
    if (atBottom) {
      setNewMessageCount(0);
    }
  }, []);

  // Auto-scroll once when the widget opens; track new messages while scrolled up.
  useEffect(() => {
    if (!messages.length) return;

    if (!hasScrolledOnOpen.current) {
      scrollToBottom();
      hasScrolledOnOpen.current = true;
      lastSeenIdRef.current = messages[messages.length - 1]._id;
      return;
    }

    const lastSeenId = lastSeenIdRef.current;
    const latestId = messages[messages.length - 1]._id;

    if (lastSeenId && lastSeenId !== latestId && !isAtBottomRef.current) {
      const lastSeenIndex = messages.findIndex((m) => m._id === lastSeenId);
      const added =
        lastSeenIndex === -1
          ? messages.length
          : messages.length - 1 - lastSeenIndex;

      if (added > 0) {
        setNewMessageCount((c) => c + added);
      }
    }

    lastSeenIdRef.current = latestId;
  }, [messages]);

  if (messages.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center p-8 h-full">
        <div className="text-center">
          <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-6 h-6 text-blue-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
              />
            </svg>
          </div>
          <h3 className="text-sm font-medium text-gray-800 mb-1">
            {t("chatWidget.messageList.noMessagesYet")}
          </h3>
          <p className="text-xs text-gray-600">
            {t("chatWidget.messageList.startTheConversation")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex-1 h-full overflow-hidden">
      <div
        ref={scrollContainerRef}
        onScroll={handleScroll}
        className="overflow-y-auto p-2 pr-0 h-full scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent"
      >
        {messages.map((message, index) => (
          <Message
            key={message._id}
            message={message}
            isOwn={message.authorId === currentUserId}
            showAvatar={
              index === 0 ||
              messages[index - 1].authorId !== message.authorId ||
              message.timestamp - messages[index - 1].timestamp > 300000 // 5 minutes
            }
          />
        ))}
        <div ref={messagesEndRef} />
      </div>

      {!isAtBottom && (
        <button
          onClick={handleScrollToBottomClick}
          className="absolute bottom-2 right-2 bg-[#0659B5] hover:bg-[#0659B5]/80 text-white rounded-full p-1.5 shadow-lg transition-opacity"
        >
          <ChevronDown className="h-5 w-5" />
          {newMessageCount > 0 && (
            <span className="absolute -top-1.5 -right-2 bg-red-500 text-white text-xs font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
              {newMessageCount < 100 ? newMessageCount : "99+"}
            </span>
          )}
        </button>
      )}
    </div>
  );
}
